/**
 * Every openable menu in play, as one focused `@annwn/ui` island (RC1-05,
 * finished in RC2-07).
 *
 * This is the React *island* M6-05 named as the proof of the focus handoff. The
 * camp menu was a line of prompt text with the game intercepting the next
 * keystroke; it is now a modal `Popup` over a `Menu`, so the keyboard is
 * genuinely *in the overlay* while it is open and genuinely back on the canvas
 * when it closes.
 *
 * RC2-07 folded the second menu into it. The tile context menu — what you may
 * do to the thing under the cursor — is the *same* component with a different
 * list, because two menus are two focus models, two key conventions and two
 * places for a verb to go missing. `Game` decides which list; this decides
 * nothing.
 *
 * The two halves of the handoff:
 *
 * - **The overlay takes the keyboard.** `Popup` traps Tab and, on open, focus
 *   moves inside; this island then focuses the selected option so the `Menu`'s
 *   single-key select works from the first keystroke. On close, `Popup` restores
 *   focus to whatever had it before.
 * - **The game lets go of it.** `Game` suspends its own input while the menu is
 *   open (the same `input.setEnabled(false)` the chat line uses), so a movement
 *   key pressed at the menu moves nobody. This island owns none of that — it
 *   only reports the chosen id and the cancel.
 *
 * It holds no game state and knows no rules: `Game` hands it already-built
 * `MenuItem`s (available verbs with their note, refused ones greyed with their
 * reason) and gets back an id or a cancel.
 */

import { useEffect, useRef, useState } from 'react';
import { createRoot, type Root } from 'react-dom/client';

import { Menu, Popup, type MenuItem } from '@annwn/ui';

/**
 * The id the cancel row reports.
 *
 * A row rather than only the window's close button, because the design's
 * context menu shows the way out *in the list* — a player who opened a menu by
 * accident should be able to read how to leave it rather than have to know that
 * Escape works. The id carries a space so no verb can ever collide with it.
 */
export const MENU_CANCEL_ID = ' cancel';

/**
 * The way out. No `key`, deliberately: Escape closes the popup and the row says
 * so in its meta, and giving it a letter would put a cancel one mistyped
 * keystroke away from every verb above it.
 */
const CANCEL_ITEM: MenuItem = { id: MENU_CANCEL_ID, name: 'cancel', meta: 'esc' };

export interface ActionMenuRequest {
  readonly title: string;

  /** What the menu is about, when the title alone does not say it. */
  readonly subtitle?: string;
  readonly items: readonly MenuItem[];
}

export interface ActionMenuOptions {
  /** A row was chosen — its `MenuItem` id. */
  onChoose(id: string): void;
  /** The menu was dismissed without a choice (Escape, backdrop, or the close). */
  onCancel(): void;
}

interface PanelProps extends ActionMenuOptions {
  readonly request: ActionMenuRequest;
}

function ActionMenuPanel({ request, onChoose, onCancel }: PanelProps) {
  const items = [...request.items, CANCEL_ITEM];
  const [selectedId, setSelectedId] = useState<string | undefined>(
    () => request.items.find((item) => !item.disabled)?.id ?? MENU_CANCEL_ID,
  );
  const wrap = useRef<HTMLDivElement>(null);

  // Move focus onto the selected option once the popup is up, so a single-key
  // select lands from the first keystroke and the keyboard is unambiguously in
  // the overlay. Runs after `Popup`'s own open-focus effect (parent after child).
  useEffect(() => {
    const option =
      wrap.current?.querySelector<HTMLElement>('[role="option"][tabindex="0"]') ??
      wrap.current?.querySelector<HTMLElement>('[role="option"]');
    option?.focus();
  }, []);

  return (
    <Popup
      open
      title={request.title}
      onClose={onCancel}
      {...(request.subtitle === undefined ? {} : { description: request.subtitle })}
    >
      <div className="menu-overlay" ref={wrap}>
        <Menu
          items={items}
          selectedId={selectedId}
          singleKeySelect
          label={request.title}
          onSelect={(item) => {
            if (item.disabled) return;
            if (item.id === MENU_CANCEL_ID) {
              onCancel();
              return;
            }
            setSelectedId(item.id);
            onChoose(item.id);
          }}
        />
      </div>
    </Popup>
  );
}

export interface ActionMenuHandle {
  open(request: ActionMenuRequest): void;
  close(): void;
  destroy(): void;
}

/**
 * Mount the menu island and return the imperative handle `Game` drives through
 * the shell. `open` shows the popup; `close` tears it down, which unmounts the
 * panel and lets `Popup` restore focus to the canvas.
 */
export function mountActionMenu(
  container: HTMLElement,
  options: ActionMenuOptions,
): ActionMenuHandle {
  const root: Root = createRoot(container);
  let request: ActionMenuRequest | null = null;

  const render = (): void => {
    root.render(
      request ? (
        <ActionMenuPanel
          // A fresh panel per request, so opening the context menu on a second
          // tile starts on that tile's first verb rather than remembering the
          // selection made on the last one.
          key={`${request.title}:${request.items.map((item) => item.id).join(',')}`}
          request={request}
          onChoose={options.onChoose}
          onCancel={options.onCancel}
        />
      ) : null,
    );
  };

  render();

  return {
    open(next) {
      request = next;
      render();
    },
    close() {
      if (request === null) return;
      request = null;
      render();
    },
    destroy() {
      root.unmount();
    },
  };
}
